import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook, Twitter, Globe } from 'lucide-react';
import Logo from './Logo';
import { COMPANY } from '../data/mock';
import { toast } from 'sonner';

const NAV_GROUPS = [
  { label: 'Industries', to: '/industries' },
  { label: 'Careers', to: '/about' },
  { label: 'AI System', to: '/ai-system' },
  { label: 'Blog', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Contact', to: '/contact' },
  { label: 'Team Builder', to: '/services' },
  { label: 'About', to: '/about' },
];

const SOCIAL_LINKS = [
  { icon: Instagram, href: '#', label: 'Instagram' },
  { icon: Linkedin, href: '#', label: 'LinkedIn' },
  { icon: Facebook, href: '#', label: 'Facebook' },
  { icon: Twitter, href: '#', label: 'Twitter' },
];

const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [agree, setAgree] = useState(false);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!email) return toast.error('Please enter your email');
    if (!agree) return toast.error('Please agree to subscribe');
    toast.success('Subscribed! Welcome to Geekynd India.');
    setEmail('');
    setAgree(false);
  }, [email, agree, setEmail, setAgree]);

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      <p className="font-display font-semibold text-white mb-3">Subscribe to our newsletter</p>
      <label className="block text-xs text-white/60 mb-1">Email *</label>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full bg-transparent border border-[#1AB6FF]/25 focus:border-[#1AB6FF] outline-none rounded-md px-3 py-2.5 text-sm text-white"
      />
      <label className="mt-3 flex items-center gap-2 text-sm text-white/70 cursor-pointer">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="accent-[#1AB6FF]" />
        Yes, subscribe me to your newsletter.
      </label>
      <button type="submit" className="mt-4 w-full btn-cyan rounded-md py-2.5 text-sm font-semibold">
        Submit
      </button>
    </form>
  );
};

const BrandColumn = () => (
  <div>
    <Logo />
    <p className="text-white/60 mt-6 text-sm leading-relaxed max-w-sm">
      Your growth partner for virtual assistants, AI systems and business operations&mdash;
      built for ambitious Indian businesses. We help you save time, reduce costs, and scale with confidence.
    </p>
    <NewsletterForm />
  </div>
);

const NavColumn = () => (
  <div>
    <h4 className="font-display font-semibold text-lg text-white border-b border-[#1AB6FF]/40 pb-2 inline-block mb-6">
      Navigation
    </h4>
    <div className="grid grid-cols-2 gap-y-3 gap-x-8 text-sm">
      {NAV_GROUPS.map((g) => (
        <Link key={g.label} to={g.to} className="text-white/75 hover:text-[#1AB6FF]">
          {g.label}
        </Link>
      ))}
      <Link to="/services" className="text-white/75 hover:text-[#1AB6FF] col-span-2">Lead Generation</Link>
    </div>
  </div>
);

const ContactRow = ({ icon: Icon, title, children }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 rounded-md bg-[#0D1320] border border-[#1AB6FF]/20 flex items-center justify-center shrink-0">
      <Icon size={18} className="text-[#1AB6FF]" />
    </div>
    <div>
      <p className="font-display font-semibold text-white">{title}</p>
      {children}
    </div>
  </div>
);

const ContactColumn = () => (
  <div>
    <h4 className="font-display font-semibold text-lg text-white border-b border-[#1AB6FF]/40 pb-2 inline-block mb-6">
      Contact us
    </h4>
    <div className="space-y-5">
      <ContactRow icon={Mail} title="Email us">
        <a href={`mailto:${COMPANY.email}`} className="text-sm text-white/70 hover:text-[#1AB6FF] underline-offset-2 hover:underline">{COMPANY.email}</a>
      </ContactRow>
      <ContactRow icon={Phone} title="WhatsApp">
        <p className="text-sm text-white/70">{COMPANY.whatsapp}</p>
      </ContactRow>
      <ContactRow icon={MapPin} title="Locations">
        <p className="text-sm text-white/70">{COMPANY.locations.join('  |  ')}</p>
      </ContactRow>
      <ContactRow icon={Globe} title="Follow us">
        <div className="flex items-center gap-3 mt-1.5">
          {SOCIAL_LINKS.map((s) => (
            <a key={s.label} href={s.href} aria-label={s.label} className="text-white/70 hover:text-[#1AB6FF]">
              <s.icon size={20} />
            </a>
          ))}
        </div>
      </ContactRow>
    </div>
  </div>
);

const Footer = () => (
  <footer className="relative bg-[#04070d] border-t border-[#1AB6FF]/10 pt-20 pb-8">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
        <BrandColumn />
        <NavColumn />
        <ContactColumn />
      </div>

      <div className="mt-16 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/55">
        <span>Privacy Policy</span>
        <span>{COMPANY.copyright}</span>
        <span>Terms of Services</span>
      </div>
    </div>
  </footer>
);

export default Footer;
